import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faCalculator, faCheckDouble, faEye } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import SpanSelector from '../components/SpanSelector';
import SpanSelectorTable from '../components/SpanSelectorTable';
import { useCustomLoading } from '../hooks/useCustomLoading';

/**
 * CreateRepairProject Page
 * 
 * Create new span-based repair project with:
 * - Bridge selection
 * - Span selection (unlimited)
 * - Project details (name, year, crews)
 * - Automatic mobilization cost calculation
 * - Scaffolding configuration
 * - Bulk selection options
 */
const CreateRepairProject = () => {
  const navigate = useNavigate();
  const { withLoading } = useCustomLoading();

  const [bridges, setBridges] = useState([]);
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [selectedSpans, setSelectedSpans] = useState([]);
  const [error, setError] = useState(null);
  const [estimatedCosts, setEstimatedCosts] = useState(null);
  const [selectEntireProject, setSelectEntireProject] = useState(false);
  const [useTableView, setUseTableView] = useState(false); // Default to improved card view
  const [inspectionData, setInspectionData] = useState(null);
  const [areaDataLevelLabel, setAreaDataLevelLabel] = useState('Elements'); // Default label

  // Form data
  const [formData, setFormData] = useState({
    bridgeId: '',
    projectName: '',
    projectCode: '',
    description: '',
    plannedYear: new Date().getFullYear(),
    plannedStartDate: '',
    plannedEndDate: '',
    crewsCount: 1,
    scaffoldingSitesCount: 1,
    scaffoldingAreaPerSite: 1000,
    priority: 'medium',
    useInspectionData: false,
    notes: ''
  });

  useEffect(() => {
    fetchBridges();
  }, []);

  // Fetch bridge level configuration when bridge is selected
  useEffect(() => {
    if (formData.bridgeId) {
      fetchBridgeLevelConfig(formData.bridgeId);
    } else {
      setAreaDataLevelLabel('Elements');
    }
  }, [formData.bridgeId]);

  const fetchBridgeLevelConfig = async (bridgeId) => {
    try {
      const response = await api.get(`/bridges/${bridgeId}`);
      if (response.data.success) {
        const bridge = response.data.data;
        const levelLabels = bridge.levelLabels || {};
        
        // Find the level that holds area data
        let areaLevelLabel = 'Elements'; // Default
        for (const [levelKey, levelConfig] of Object.entries(levelLabels)) {
          if (levelConfig?.holdsAreaData) {
            areaLevelLabel = levelConfig.label || 'Elements';
            break;
          }
        }
        
        setAreaDataLevelLabel(areaLevelLabel);
      }
    } catch (err) {
      console.error('Error fetching bridge level config:', err);
      // Keep default label on error
    }
  };

  // Clear costs when bridge changes
  useEffect(() => {
    if (!formData.bridgeId) {
      setEstimatedCosts(null);
    }
  }, [formData.bridgeId]);

  // Calculate estimated costs when spans or crew data changes
  useEffect(() => {
    if (selectedSpans.length > 0) {
      calculateEstimatedCosts();
    } else {
      setEstimatedCosts(null); // Clear costs when no spans selected
    }
  }, [selectedSpans, formData.crewsCount, formData.scaffoldingSitesCount, formData.scaffoldingAreaPerSite]);

  const fetchBridges = async () => {
    try {
      const response = await api.get('/bridges?limit=100');
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching bridges:', err);
      setError('Failed to fetch bridges');
    }
  };

  const handleSpanSelection = (spanIds, spans) => {
    setSelectedSpanIds(spanIds);
    setSelectedSpans(spans);
    // If spans are manually selected, turn off entire project mode
    if (spanIds.length > 0) {
      setSelectEntireProject(false);
    }
    // Fetch inspection data for selected spans
    if (spanIds.length > 0) {
      fetchInspectionData(spanIds);
    } else {
      setInspectionData(null);
    }
  };

  const fetchInspectionData = async (spanIds) => {
    try {
      // Call the analyze API to get inspection data for selected elements
      const response = await api.get(`/repair-projects/analyze?element_ids=${spanIds.join(',')}`);
      if (response.data.success) {
        setInspectionData(response.data.data);
      }
    } catch (error) {
      console.log('Inspection data not available:', error.message);
      setInspectionData(null);
    }
  };

  const handleEntireProjectToggle = async () => {
    if (!formData.bridgeId) return;
    
    if (!selectEntireProject) {
      // Select entire project - fetch all elements for the bridge
      try {
        const response = await api.get(`/repair-projects/available-spans?bridge_id=${formData.bridgeId}`);
        if (response.data.success) {
          const allSpans = response.data.data;
          const allSpanIds = allSpans.map(span => span.id);
          setSelectedSpanIds(allSpanIds);
          setSelectedSpans(allSpans);
          setSelectEntireProject(true);
        }
      } catch (error) {
        console.error('Error fetching all elements:', error);
        setError(`Failed to load all ${areaDataLevelLabel.toLowerCase()} for the bridge`);
      }
    } else {
      // Deselect entire project
      setSelectedSpanIds([]);
      setSelectedSpans([]);
      setSelectEntireProject(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateEstimatedCosts = () => {
    // Don't calculate if no spans are selected
    if (selectedSpans.length === 0) {
      setEstimatedCosts(null);
      return;
    }

    const totalArea = selectedSpans.reduce((sum, span) => sum + (parseFloat(span.total_area) || 0), 0);
    
    // Calculate scaffolding moves
    const scaffoldingMoves = Math.max(0, Math.ceil(totalArea / formData.scaffoldingAreaPerSite) - 1);
    
    // Estimate duration (50 m² per crew per day, 5 days per week)
    const productivityRate = 50;
    const daysRequired = Math.ceil(totalArea / (productivityRate * formData.crewsCount));
    const weeksRequired = Math.ceil(daysRequired / 5);
    const weeksWithBuffer = Math.ceil(weeksRequired * 1.2); // 20% buffer

    // Estimate mobilization costs (simplified)
    const workersCost = 150000 * formData.crewsCount;
    const transportCost = 25000;
    const scaffoldingSetup = 45000 * formData.scaffoldingSitesCount;
    const scaffoldingMovesCost = 15000 * scaffoldingMoves;
    const scaffoldingRental = 2500 * weeksWithBuffer * formData.scaffoldingSitesCount;
    const equipmentCost = 35000;
    const safetyCost = 18000;
    const logisticsCost = 22000;

    const totalMobilization = workersCost + transportCost + scaffoldingSetup + 
                              scaffoldingMovesCost + scaffoldingRental + 
                              equipmentCost + safetyCost + logisticsCost;

    setEstimatedCosts({
      totalArea,
      scaffoldingMoves,
      daysRequired,
      weeksRequired,
      weeksWithBuffer,
      mobilization: {
        workers: workersCost,
        transport: transportCost,
        scaffoldingSetup,
        scaffoldingMoves: scaffoldingMovesCost,
        scaffoldingRental,
        equipment: equipmentCost,
        safety: safetyCost,
        logistics: logisticsCost,
        total: totalMobilization
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.bridgeId) {
      setError('Please select a bridge');
      return;
    }

    if (!formData.projectName.trim()) {
      setError('Please enter a project name');
      return;
    }

    if (selectedSpanIds.length === 0) {
      setError(`Please select at least one ${areaDataLevelLabel.toLowerCase()}`);
      return;
    }

    try {
      await withLoading(async () => {
        const response = await api.post('/repair-projects', {
          ...formData,
          spanIds: selectedSpanIds
        });

        if (response.data.success) {
          navigate(`/repair-projects/${response.data.data.id}`);
        }
      }, 'Creating repair project...');
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.response?.data?.message || 'Failed to create repair project');
    }
  };

  const totalArea = selectedSpans.reduce((sum, span) => sum + (parseFloat(span.total_area) || 0), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/repair-projects')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Repair Projects
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900">Create Repair Project</h1>
        <p className="text-gray-600 mt-1">Select {areaDataLevelLabel.toLowerCase()} for repair</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Project Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bridge Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bridge <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.bridgeId}
                onChange={(e) => handleInputChange('bridgeId', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a bridge</option>
                {bridges.map(bridge => (
                  <option key={bridge.id} value={bridge.id}>{bridge.bridgeName}</option>
                ))}
              </select>
            </div>

            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.projectName}
                onChange={(e) => handleInputChange('projectName', e.target.value)}
                required
                placeholder="e.g., 2025 Northbound Repair"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Project Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Code</label>
              <input
                type="text"
                value={formData.projectCode}
                onChange={(e) => handleInputChange('projectCode', e.target.value)}
                placeholder="Optional unique code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Planned Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Planned Year</label>
              <input
                type="number"
                value={formData.plannedYear}
                onChange={(e) => handleInputChange('plannedYear', parseInt(e.target.value))}
                min={new Date().getFullYear()}
                max={new Date().getFullYear() + 10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                placeholder="Project description..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Span Selection */}
        {formData.bridgeId && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Select {areaDataLevelLabel} <span className="text-red-500">*</span>
              </h2>
              
              <div className="flex items-center space-x-4">
                {/* View Toggle */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">View:</span>
                  <button
                    onClick={() => setUseTableView(false)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      !useTableView 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Cards
                  </button>
                  <button
                    onClick={() => setUseTableView(true)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      useTableView 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Table
                  </button>
                </div>
                
                {/* Entire Project Selection Toggle */}
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectEntireProject}
                      onChange={handleEntireProjectToggle}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Select Entire Bridge
                    </span>
                  </label>
                  {selectEntireProject && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      All {areaDataLevelLabel.toLowerCase()} selected
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {!selectEntireProject && (
              <>
                {useTableView ? (
                  <SpanSelectorTable
                    bridgeId={formData.bridgeId}
                    onSelectionChange={handleSpanSelection}
                    levelLabel={areaDataLevelLabel}
                  />
                ) : (
                  <SpanSelector
                    bridgeId={formData.bridgeId}
                    onSelectionChange={handleSpanSelection}
                    levelLabel={areaDataLevelLabel}
                  />
                )}
                
                {/* Inspection Data Summary */}
                {inspectionData && selectedSpanIds.length > 0 && (
                  <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">
                      <FontAwesomeIcon icon={faEye} className="mr-2" />
                      Inspection Data Available
                    </h4>
                    <div className="text-sm text-green-700">
                      <p>Inspection data is available for {inspectionData.elements?.filter(e => e.inspection?.hasInspectionData).length || inspectionData.spans?.filter(s => s.inspection).length || 0} of {selectedSpanIds.length} selected {areaDataLevelLabel.toLowerCase()}.</p>
                      <p className="mt-1">This data will be used to enhance repair planning accuracy.</p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {selectEntireProject && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faCheckDouble} className="text-green-600 mr-2" />
                    <div>
                    <h3 className="text-green-800 font-medium">Entire Bridge Selected</h3>
                    <p className="text-green-700 text-sm mt-1">
                      All {selectedSpans.length} {areaDataLevelLabel.toLowerCase()} from this bridge will be included in the repair project.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleEntireProjectToggle}
                  className="mt-3 px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                >
                  Switch to Manual Selection
                </button>
              </div>
            )}
          </div>
        )}

        {/* Resources & Scheduling */}
        {selectedSpans.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Resources & Scheduling</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Crews */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Crews</label>
                <input
                  type="number"
                  value={formData.crewsCount}
                  onChange={(e) => handleInputChange('crewsCount', parseInt(e.target.value) || 1)}
                  min={1}
                  max={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Scaffolding Sites */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scaffolding Sites</label>
                <input
                  type="number"
                  value={formData.scaffoldingSitesCount}
                  onChange={(e) => handleInputChange('scaffoldingSitesCount', parseInt(e.target.value) || 1)}
                  min={1}
                  max={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Scaffolding Area per Site */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scaffolding Area (m²)
                </label>
                <input
                  type="number"
                  value={formData.scaffoldingAreaPerSite}
                  onChange={(e) => handleInputChange('scaffoldingAreaPerSite', parseInt(e.target.value) || 1000)}
                  min={100}
                  step={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Estimated Costs */}
        {estimatedCosts && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faCalculator} />
              Estimated Costs & Duration
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Project Summary */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Project Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Area:</span>
                    <span className="font-medium">{estimatedCosts.totalArea.toLocaleString()}m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Duration:</span>
                    <span className="font-medium">{estimatedCosts.weeksWithBuffer} weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scaffolding Moves:</span>
                    <span className="font-medium">{estimatedCosts.scaffoldingMoves}</span>
                  </div>
                </div>
              </div>

              {/* Mobilization Costs */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Mobilization Cost Breakdown</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Workers:</span>
                    <span className="font-medium">${estimatedCosts.mobilization.workers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scaffolding:</span>
                    <span className="font-medium">
                      ${(estimatedCosts.mobilization.scaffoldingSetup + 
                         estimatedCosts.mobilization.scaffoldingMoves + 
                         estimatedCosts.mobilization.scaffoldingRental).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Equipment & Safety:</span>
                    <span className="font-medium">
                      ${(estimatedCosts.mobilization.equipment + 
                         estimatedCosts.mobilization.safety).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="text-gray-900 font-semibold">Total Mobilization:</span>
                    <span className="text-blue-600 font-bold">
                      ${estimatedCosts.mobilization.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/repair-projects')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={selectedSpanIds.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faSave} />
            Create Project
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateRepairProject;

